import React from 'react';

// Build a compact page list with ellipses, e.g. [1, '…', 24, 25, 26, '…', 50].
// Always shows first/last page plus a window around the current page.
const buildPageItems = (page, totalPages, siblings = 1) => {
  const totalNumbers = siblings * 2 + 5; // first + last + current + 2*siblings + 2 ellipses
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, totalPages);
  const showLeftDots = left > 2;
  const showRightDots = right < totalPages - 1;

  const items = [1];
  if (showLeftDots) items.push('left-ellipsis');
  for (let p = showLeftDots ? left : 2; p <= (showRightDots ? right : totalPages - 1); p++) {
    items.push(p);
  }
  if (showRightDots) items.push('right-ellipsis');
  items.push(totalPages);
  return items;
};

const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const items = buildPageItems(page, totalPages);

  return (
    <div className="flex flex-wrap justify-end items-center mt-4 gap-2">
      <button
        className="px-3 py-1 rounded border bg-white disabled:opacity-50"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
        data-testid="pagination-prev"
      >
        Prev
      </button>
      {items.map((item) =>
        typeof item === 'string' ? (
          <span key={item} className="px-2 py-1 text-gray-400 select-none">
            …
          </span>
        ) : (
          <button
            key={item}
            className={`px-3 py-1 rounded border ${page === item ? 'bg-primary-600 text-white' : 'bg-white'}`}
            // The fill alone says nothing to a screen reader.
            aria-current={page === item ? 'page' : undefined}
            onClick={() => onPageChange(item)}
            data-testid={`pagination-page-${item}`}
          >
            {item}
          </button>
        )
      )}
      <button
        className="px-3 py-1 rounded border bg-white disabled:opacity-50"
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages}
        data-testid="pagination-next"
      >
        Next
      </button>
    </div>
  );
};

export default Pagination;
